import React, { FC } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";

export interface Recommendation {
  id: number;
  category: string;
  name: string;
  blurb: string | null;
  image_url: string | null;
  latitude: string | number | null;
  longitude: string | number | null;
  website_url: string | null;
  is_sponsored: boolean;
}

interface Props {
  propertyData: PropertyToEdit & { recommendations?: Recommendation[] };
}

const CATEGORY_ICONS: Record<string, string> = {
  groceries: "la-shopping-cart",
  cafe: "la-coffee",
  restaurant: "la-utensils",
  gym: "la-dumbbell",
  pharmacy: "la-clinic-medical",
  laundry: "la-tshirt",
  outdoors: "la-tree",
  coworking: "la-laptop",
  transport: "la-bus",
  nightlife: "la-glass-cheers",
  shopping: "la-shopping-bag",
};

const CATEGORY_LABELS: Record<string, string> = {
  groceries: "Groceries",
  cafe: "Café",
  restaurant: "Restaurant",
  gym: "Gym",
  pharmacy: "Pharmacy",
  laundry: "Laundry",
  outdoors: "Outdoors",
  coworking: "Coworking",
  transport: "Transport",
  nightlife: "Nightlife",
  shopping: "Shopping",
};

const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Straight-line distance with a 1.35 detour factor, walked at 80m/min.
// Accurate to within a minute or two, which is all a listing page needs.
const walkMinutes = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
): number => {
  const R = 6371000;
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = rad(toLat - fromLat);
  const dLng = rad(toLng - fromLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(fromLat)) * Math.cos(rad(toLat)) * Math.sin(dLng / 2) ** 2;
  const meters = 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  return Math.max(1, Math.round((meters * 1.35) / 80));
};

const CategoryTile: FC<{ category: string }> = ({ category }) => (
  <div className="w-full h-40 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800">
    <i
      className={`las ${
        CATEGORY_ICONS[category] || "la-map-marker-alt"
      } text-5xl text-neutral-400 dark:text-neutral-500`}
      aria-hidden="true"
    />
  </div>
);

const RecommendationCard: FC<{
  item: Recommendation;
  minutes: number | null;
}> = ({ item, minutes }) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const showImage = Boolean(item.image_url) && !imageFailed;

  const body = (
    <>
      <div className="relative">
        {showImage ? (
          <img
            src={item.image_url as string}
            alt={item.name}
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="w-full h-40 object-cover"
          />
        ) : (
          <CategoryTile category={item.category} />
        )}
        {item.is_sponsored && (
          <span className="absolute top-3 right-3 px-2.5 py-1 text-xs font-medium rounded-full bg-white/95 text-[#f5694b] dark:bg-neutral-900/95">
            Partner
          </span>
        )}
      </div>

      <div className="p-4">
        <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {CATEGORY_LABELS[item.category] || item.category}
        </p>
        <h3 className="mt-1 text-base font-semibold text-neutral-900 dark:text-white line-clamp-1">
          {item.name}
        </h3>
        {item.blurb && (
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300 line-clamp-3">
            {item.blurb}
          </p>
        )}
        {minutes !== null && (
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            <i className="las la-walking text-lg" aria-hidden="true" />
            {minutes} min walk
          </span>
        )}
      </div>
    </>
  );

  const cardClasses = `flex flex-col overflow-hidden rounded-2xl border ${
    item.is_sponsored
      ? "border-[#f5694b]"
      : "border-gray-300 dark:border-gray-600"
  } bg-white dark:bg-neutral-900 transition-shadow hover:shadow-lg focus-within:shadow-lg`;

  if (item.website_url) {
    return (
      <a
        href={item.website_url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className={cardClasses}
      >
        {body}
      </a>
    );
  }

  return <div className={cardClasses}>{body}</div>;
};

const Recommendations: FC<Props> = ({ propertyData }) => {
  const items = propertyData?.recommendations || [];
  if (!items.length) return null;

  const originLat = toNumber(propertyData?.latitude);
  const originLng = toNumber(propertyData?.longitude);

  const area = [propertyData?.neighborhood, propertyData?.city]
    .filter(Boolean)
    .join(", ");

  const hasSponsored = items.some((item) => item.is_sponsored);

  return (
    <div className="listingSection__wrap">
      <div>
        <h2 className="text-2xl font-semibold">Around the neighborhood</h2>
        <span className="mt-2 block text-neutral-500 dark:text-neutral-400">
          {area
            ? `Places we like within walking distance in ${area}`
            : "Places we like within walking distance"}
        </span>
      </div>

      <div className="w-full border-b border-neutral-200 dark:border-neutral-700" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item) => {
          const lat = toNumber(item.latitude);
          const lng = toNumber(item.longitude);
          const minutes =
            originLat !== null && originLng !== null && lat !== null && lng !== null
              ? walkMinutes(originLat, originLng, lat, lng)
              : null;

          return (
            <RecommendationCard key={item.id} item={item} minutes={minutes} />
          );
        })}
      </div>

      <p className="text-xs text-neutral-400 dark:text-neutral-500">
        Walking times are estimates from the property address.
        {hasSponsored && " Partner listings are paid placements."}
      </p>
    </div>
  );
};

export default Recommendations;